// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 3f0c74a1-9d52-4c1a-8e63-2b7d5a1c47e9

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Chip, Button, Tabs, Tab,
  FormControlLabel, Checkbox, Autocomplete, Collapse,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import {
  allFieldsFor, FACT_SHEET_TYPES, FIELD_TYPES, PICKLISTS, resolvePicklist, resolveDrTier,
  TAG_GROUPS, typeMeta,
} from './schema';
import { exportCsv, exportCanvasPng, printElementToPdf } from './exports';
import store from './store';

// ---------------------------------------------------------------------------
// Primordia — Portfolio (scatter/bubble) + Radar.
//
// The two report types a real LeanIX tenant leans on hardest and we did not
// have: "Application Portfolio — Axis: Functional fit vs. technical fit",
// "Application Portfolio — TIME by Criticality", and the client-authored
// "Application DR — RTO (X) & RPO (Y)".
//
// Axes are NOT hardcoded. Both tabs derive their dimension catalogue from the
// schema the same way the Application Landscape does, so any bounded field —
// including per-tenant custom ones — can be an axis the day it is added.
//
// Rules this screen holds itself to:
//   * A record with no value for a plotted field is EXCLUDED, counted, named,
//     and exported. It is never given a position it does not have, and never
//     disappears without a number next to it. In discovery the excluded set is
//     usually the finding.
//   * A picklist's own order is the axis order. "Perfect" never sorts to the
//     left of "Insufficient" because P < I alphabetically.
//   * An order is never invented. Fields whose picklist is a set of peers
//     (TIME, 6R) lay out in picklist order but are flagged "not a scale", and
//     the flag is repeated wherever the geometry would imply magnitude
//     (quadrants, radar radius).
//   * Coincident records stay visible — deterministic jitter per record, or one
//     bubble sized by the number of records at the coordinate. Either way the
//     true count is printed. Ten apps never render as one dot.
//   * Every artifact (screen, CSV, PDF, PNG) carries the same counts.
//
// Rendered as plain SVG rather than chart.js (which is in package.json and used
// by Dashboard/Benchmarks) for three reasons: categorical ordinal axes with
// per-record jitter, per-point click-through and quadrant bands are all custom
// work in chart.js; and exports.js#printElementToPdf copies innerHTML, which
// carries SVG intact but would hand the PDF a blank <canvas>. PNG still goes
// through exports.js#exportCanvasPng — the SVG is rasterised into a canvas.
// ---------------------------------------------------------------------------

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEADER = 'sectionHeader.main';

const NOT_SET = 'Not set';
const NOT_SET_COLOR = '#9AA6B2';
const UNKNOWN_COLOR = '#C6D0D9';

const ALL_TYPES = '__all';
const NONE = '__none';
const COUNT = '__count';

const MAX_POINTS = 1000;        // drawn record marks (jitter mode)
const AXIS_MAX_LEVELS = 24;     // beyond this an axis is unreadable
const COLOR_MAX_VALUES = 24;
const MAX_LEGEND = 14;
const MAX_EXCLUDED_LISTED = 250;
const MAX_RADAR_SUBJECTS = 8;
const MAX_RADAR_AXES = 8;

const PALETTE = [
  '#0D5B9A', '#021F4B', '#2E7D32', '#F5A623', '#C62828',
  '#6A1B9A', '#00838F', '#EF6C00', '#43A047', '#283593',
  '#8E24AA', '#0B5FFF',
];

const PCT_BANDS = ['0%', '1–39%', '40–74%', '≥75%'];

// Picklists that read as a progression rather than a set of peers. Only these
// may be described as a scale; the rest still lay out in picklist order but
// carry a warning wherever geometry would imply magnitude.
const SCALE_KEYS = [
  'functionalFit', 'technicalFit', 'businessCriticality', 'patientSafetyImpact',
  'rto', 'rpo', 'lifecycleState', 'cmEffort', '__completion', '__drTier',
];

const SEMANTIC_COLORS = {
  timeClassification: {
    Invest: '#2E7D32', Tolerate: '#0B5FFF', Migrate: '#F5A623', Eliminate: '#D0021B',
  },
};

// The classic 2x2 reading, stated rather than assumed: X low→high left→right,
// Y low→high bottom→top, i.e. the TIME reading of functional fit vs technical fit.
const QUADRANTS = [
  { key: 'invest', label: 'Invest', right: true, top: true, color: '#2E7D32' },
  { key: 'tolerate', label: 'Tolerate', right: false, top: true, color: '#0B5FFF' },
  { key: 'migrate', label: 'Migrate', right: true, top: false, color: '#F5A623' },
  { key: 'eliminate', label: 'Eliminate', right: false, top: false, color: '#D0021B' },
];

// ---- small helpers ---------------------------------------------------------
const num = (n) => Number(n || 0).toLocaleString();
const pct = (n, total) => (total ? Math.round((n / total) * 1000) / 10 : 0);
const trunc = (s, n) => (String(s).length > n ? `${String(s).slice(0, n - 1)}…` : String(s));
const safeName = (s) => String(s).replace(/[^a-zA-Z0-9.\-_]/g, '_');

// '' means "this record has no value" — the whole honesty contract of the
// screen rests on missing being distinguishable from a real value.
const rawText = (v) => {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    return v.filter((x) => x !== null && x !== undefined && String(x).trim() !== '').join(', ');
  }
  return String(v).trim();
};

const fieldText = (fs, key) => rawText(fs.fields ? fs.fields[key] : undefined);

const tagText = (fs, group) => {
  const vals = (fs.tags || []).filter((t) => t && t.group === group && t.value).map((t) => t.value);
  return vals.join(', ');
};

const numberOf = (fs, key) => {
  const v = fs.fields ? fs.fields[key] : undefined;
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// A percent never captured is not a percent of zero.
const bandPercent = (v) => {
  const n = Number(v);
  if (v === undefined || v === null || v === '' || !Number.isFinite(n)) return '';
  if (n <= 0) return PCT_BANDS[0];
  if (n < 40) return PCT_BANDS[1];
  if (n < 75) return PCT_BANDS[2];
  return PCT_BANDS[3];
};

const yearOf = (v) => {
  const m = String(v === undefined || v === null ? '' : v).match(/(\d{4})/);
  return m ? m[1] : '';
};

// Deterministic per-record jitter: the same fact sheet lands in the same spot on
// every render and every export, so a screenshot in a deck stays comparable.
const hashUnit = (s) => {
  let h = 2166136261;
  const str = String(s);
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
};

// ---- dimension catalogue ---------------------------------------------------
const UNCHARTABLE = {
  [FIELD_TYPES.TEXTAREA]: 'long-form text — not a bounded set',
  [FIELD_TYPES.NUMBER]: 'continuous number — offered as a bubble size measure instead',
  [FIELD_TYPES.CURRENCY]: 'currency amount — offered as a bubble size measure instead',
  [FIELD_TYPES.LIFECYCLE]: 'timeline field — use Lifecycle State instead',
  [FIELD_TYPES.RELATION]: 'relationship, not a value',
  [FIELD_TYPES.TAGS]: 'use the "Tag: …" dimensions',
  [FIELD_TYPES.MULTISELECT]: 'multiple values per record — no single position',
};

const BOUNDED_TYPES = [FIELD_TYPES.SELECT, FIELD_TYPES.BOOLEAN, FIELD_TYPES.ARROWS, FIELD_TYPES.RATING];

function buildDimensions(typeKey, tagGroups) {
  const out = [];

  out.push({
    key: '__type',
    label: 'Fact Sheet Type',
    section: 'Core',
    kind: 'bounded',
    order: FACT_SHEET_TYPES.map((t) => t.label),
    scale: false,
    orderNote: 'schema order',
    get: (fs) => (fs.type ? typeMeta(fs.type).label : ''),
  });
  out.push({
    key: '__drTier',
    label: 'DR Tier (tag + field reconciled)',
    section: 'Core',
    kind: 'bounded',
    order: PICKLISTS.tier,
    scale: true,
    orderNote: 'picklist order',
    get: (fs) => resolveDrTier(fs) || '',
  });
  out.push({
    key: '__completion',
    label: 'Data Completion',
    section: 'Core',
    kind: 'bounded',
    order: PCT_BANDS,
    scale: true,
    orderNote: 'banded',
    get: (fs) => bandPercent(fs.completion),
  });

  // DR Tier is deliberately absent from the tag list: reading the tag alone
  // drops every record carrying the value in fields.drTier.
  tagGroups.filter((g) => g !== 'DR Tier').forEach((g) => {
    out.push({
      key: `__tag:${g}`,
      label: `Tag: ${g}`,
      section: 'Tags',
      kind: 'bounded',
      order: null,
      scale: false,
      get: (fs) => tagText(fs, g),
    });
  });

  const typeKeys = typeKey === ALL_TYPES ? FACT_SHEET_TYPES.map((t) => t.key) : [typeKey];
  const seen = { name: true, drTier: true }; // name = identity, drTier folded into __drTier
  typeKeys.forEach((tk) => {
    allFieldsFor(tk).forEach((f) => {
      if (seen[f.key]) return;
      seen[f.key] = true;
      const base = { key: f.key, label: f.label, section: f.section || 'Fields' };
      const picklist = f.picklist ? resolvePicklist(f.picklist) : null;
      const isScale = f.type === FIELD_TYPES.RATING || f.type === FIELD_TYPES.ARROWS
        || SCALE_KEYS.indexOf(f.key) !== -1;

      if (BOUNDED_TYPES.indexOf(f.type) !== -1) {
        out.push({
          ...base,
          kind: 'bounded',
          order: f.type === FIELD_TYPES.BOOLEAN ? ['No', 'Yes'] : (picklist && picklist.length ? picklist : null),
          scale: f.type === FIELD_TYPES.BOOLEAN ? true : isScale,
          orderNote: f.type === FIELD_TYPES.BOOLEAN ? 'No → Yes' : 'picklist order',
          get: (fs) => fieldText(fs, f.key),
        });
        return;
      }
      if (f.type === FIELD_TYPES.PERCENT) {
        out.push({
          ...base, kind: 'bounded', order: PCT_BANDS, scale: true, orderNote: 'banded',
          get: (fs) => bandPercent(fs.fields ? fs.fields[f.key] : undefined),
        });
        return;
      }
      if (f.type === FIELD_TYPES.DATE) {
        out.push({
          ...base,
          label: `${f.label} (year)`,
          kind: 'bounded',
          order: null,
          numericSort: true,
          scale: true,
          orderNote: 'chronological',
          get: (fs) => yearOf(fs.fields ? fs.fields[f.key] : undefined),
        });
        return;
      }
      if (f.type === FIELD_TYPES.TEXT || f.type === FIELD_TYPES.URL) {
        out.push({
          ...base, kind: 'bounded', order: null, scale: false, freeText: true,
          get: (fs) => fieldText(fs, f.key),
        });
        return;
      }
      out.push({
        ...base,
        kind: 'unavailable',
        order: null,
        scale: false,
        reason: UNCHARTABLE[f.type] || `${f.type} field — not a bounded set`,
        get: () => '',
      });
    });
  });

  return out;
}

// Numeric measures usable for bubble size. Continuous fields are useless as an
// axis but are exactly what a portfolio bubble wants.
function buildMeasures(typeKey) {
  const out = [
    { key: NONE, label: 'Uniform size', section: 'Core', get: () => null },
    { key: COUNT, label: 'Number of records at this coordinate', section: 'Core', get: () => null },
    {
      key: '__completionPct',
      label: 'Data completion %',
      section: 'Core',
      unit: '%',
      get: (fs) => (typeof fs.completion === 'number' ? fs.completion : null),
    },
  ];
  const typeKeys = typeKey === ALL_TYPES ? FACT_SHEET_TYPES.map((t) => t.key) : [typeKey];
  const seen = {};
  typeKeys.forEach((tk) => {
    allFieldsFor(tk).forEach((f) => {
      if (seen[f.key]) return;
      if (f.type !== FIELD_TYPES.NUMBER && f.type !== FIELD_TYPES.CURRENCY && f.type !== FIELD_TYPES.PERCENT) return;
      seen[f.key] = true;
      out.push({
        key: f.key,
        label: f.label,
        section: f.section || 'Fields',
        unit: f.type === FIELD_TYPES.CURRENCY ? '$' : (f.type === FIELD_TYPES.PERCENT ? '%' : ''),
        get: (fs) => numberOf(fs, f.key),
      });
    });
  });
  return out;
}

// ---- axis levels -----------------------------------------------------------
// The picklist is the axis, in the picklist's own order, INCLUDING values no
// record uses (an empty column is information). Values a record holds that the
// picklist has never heard of are appended at the end and reported — dropping
// them would silently delete records from the chart.
function levelsFor(dim, rows) {
  const counts = new Map();
  if (dim) {
    for (let i = 0; i < rows.length; i += 1) {
      const v = dim.get(rows[i]);
      if (v) counts.set(v, (counts.get(v) || 0) + 1);
    }
  }
  const alpha = (a, b) => (dim && dim.numericSort
    ? (Number(a) - Number(b)) || String(a).localeCompare(String(b))
    : String(a).localeCompare(String(b)));

  const defined = (dim && dim.order) ? dim.order.filter((v, i, arr) => arr.indexOf(v) === i) : [];
  const extras = [...counts.keys()].filter((v) => defined.indexOf(v) === -1).sort(alpha);
  const levels = defined.concat(extras);
  return {
    levels,
    index: levels.reduce((m, v, i) => { m[v] = i; return m; }, {}),
    counts,
    extras,
    orderSource: defined.length
      ? (dim.orderNote || 'defined order')
      : (dim && dim.numericSort ? 'chronological' : 'alphabetical (no order defined)'),
  };
}

// ---- colouring -------------------------------------------------------------
const scaleColor = (idx, n) => {
  const stops = ['#D0021B', '#F5A623', '#F8E71C', '#7ED321', '#2E7D32'];
  if (n <= 1) return stops[4];
  return stops[Math.round((idx / (n - 1)) * (stops.length - 1))];
};

function buildColorMap(dim, values) {
  const map = {};
  const order = dim && dim.order && dim.order.length ? dim.order : null;
  const semantic = dim ? SEMANTIC_COLORS[dim.key] : null;
  let next = 0;
  values.forEach((v) => {
    if (v === NOT_SET) { map[v] = NOT_SET_COLOR; return; }
    if (semantic && semantic[v]) { map[v] = semantic[v]; return; }
    if (dim && dim.scale && order) {
      const i = order.indexOf(v);
      map[v] = i >= 0 ? scaleColor(i, order.length) : UNKNOWN_COLOR;
      return;
    }
    map[v] = PALETTE[next % PALETTE.length];
    next += 1;
  });
  return map;
}

// ---- PNG ------------------------------------------------------------------
// exports.js#exportCanvasPng wants a canvas; the chart is SVG. Rasterise it.
// The SVG carries no external font or stylesheet reference, so the canvas is
// never tainted and toBlob() works.
function svgToPng(svgEl, filename) {
  if (!svgEl || typeof window === 'undefined') return;
  const vb = (svgEl.getAttribute('viewBox') || '0 0 1000 640').split(/\s+/).map(Number);
  const w = vb[2] || 1000;
  const h = vb[3] || 640;
  const copy = svgEl.cloneNode(true);
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('width', String(w));
  copy.setAttribute('height', String(h));
  const text = new XMLSerializer().serializeToString(copy);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = w * 2;
    canvas.height = h * 2;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    exportCanvasPng(canvas, filename);
  };
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}`;
}

// ---- scatter geometry ------------------------------------------------------
const W = 1000;
const H = 660;
const PAD_L = 176;
const PAD_R = 26;
const PAD_T = 26;
const PAD_B = 118;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

// ===========================================================================
export default function PortfolioReport({ tenantId, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [typeKey, setTypeKey] = useState('Application');

  // Portfolio tab
  const [xKey, setXKey] = useState('functionalFit');
  const [yKey, setYKey] = useState('technicalFit');
  const [sizeKey, setSizeKey] = useState(COUNT);
  const [colorKey, setColorKey] = useState('timeClassification');
  const [mode, setMode] = useState('jitter'); // 'jitter' | 'cluster'
  const [revX, setRevX] = useState(false);
  const [revY, setRevY] = useState(false);
  const [showQuadrants, setShowQuadrants] = useState(true);
  const [splitX, setSplitX] = useState(null);
  const [splitY, setSplitY] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);
  const [cell, setCell] = useState(null); // { xi, yi } in drawn coordinates

  // Radar tab
  const [radarMode, setRadarMode] = useState('sheets'); // 'sheets' | 'groups'
  const [radarAxisKeys, setRadarAxisKeys] = useState(null);
  const [pickedIds, setPickedIds] = useState([]);
  const [groupKey, setGroupKey] = useState('applicationType');
  const [showRadarGaps, setShowRadarGaps] = useState(false);

  const scatterRef = useRef(null);
  const radarRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    const filter = typeKey === ALL_TYPES ? {} : { type: typeKey };
    store.listFactSheets(tenantId, filter)
      .then((list) => { if (alive) { setRows(list || []); setLoading(false); } })
      .catch((e) => {
        if (alive) { setError(String((e && e.message) || e)); setRows([]); setLoading(false); }
      });
    return () => { alive = false; };
  }, [tenantId, typeKey]);

  useEffect(() => { setCell(null); }, [xKey, yKey, mode, typeKey, tenantId]);

  const tagGroups = useMemo(() => {
    const found = {};
    TAG_GROUPS.forEach((g) => { found[g] = true; });
    rows.forEach((fs) => (fs.tags || []).forEach((t) => { if (t && t.group) found[t.group] = true; }));
    return Object.keys(found);
  }, [rows]);

  const dimensions = useMemo(() => buildDimensions(typeKey, tagGroups), [typeKey, tagGroups]);
  const measures = useMemo(() => buildMeasures(typeKey), [typeKey]);

  // ONE pass over the estate (7,305 fact sheets in the largest tenant) with every
  // candidate dimension on the inner loop, never one scan per dimension and never
  // in render. Decides which dimensions this tenant has actually populated and
  // which would overflow an axis.
  const options = useMemo(() => {
    const measured = dimensions.filter((d) => d.kind !== 'unavailable');
    const sets = measured.map(() => new Set());
    for (let i = 0; i < rows.length; i += 1) {
      for (let j = 0; j < measured.length; j += 1) {
        const v = measured[j].get(rows[i]);
        if (v) sets[j].add(v);
      }
    }
    const distinct = {};
    measured.forEach((d, j) => { distinct[d.key] = sets[j].size; });

    return dimensions.map((d) => {
      if (d.kind === 'unavailable') {
        return { ...d, distinct: 0, axisOk: false, colorOk: false, note: d.reason };
      }
      const n = distinct[d.key] || 0;
      const defined = !!(d.order && d.order.length) || !!d.numericSort;
      const levels = Math.max(n, (d.order || []).length);
      if (n === 0) {
        return { ...d, distinct: 0, axisOk: false, colorOk: false, note: 'no values captured in this workspace yet' };
      }
      if (levels > AXIS_MAX_LEVELS) {
        return {
          ...d,
          distinct: n,
          axisOk: false,
          colorOk: n <= COLOR_MAX_VALUES,
          note: `${num(n)} distinct values — too many for an axis`,
        };
      }
      const orderNote = defined
        ? (d.scale ? `${d.orderNote || 'defined order'} · scale` : `${d.orderNote || 'defined order'} · ordered set, not a scale`)
        : 'no defined order — laid out alphabetically';
      return {
        ...d,
        distinct: n,
        axisOk: true,
        colorOk: n <= COLOR_MAX_VALUES,
        hasOrder: defined,
        note: `${n} value${n === 1 ? '' : 's'} · ${orderNote}`,
      };
    });
  }, [dimensions, rows]);

  const byKey = useMemo(() => {
    const m = {};
    options.forEach((o) => { m[o.key] = o; });
    return m;
  }, [options]);

  const axisOptions = useMemo(() => options.filter((o) => o.axisOk), [options]);
  const colorOptions = useMemo(() => options.filter((o) => o.colorOk), [options]);
  const orderedOptions = useMemo(() => axisOptions.filter((o) => o.hasOrder), [axisOptions]);

  // A dimension can stop being chartable when the type filter or the data
  // changes. Fall back visibly rather than rendering an empty chart.
  const xd = (byKey[xKey] && byKey[xKey].axisOk) ? byKey[xKey] : axisOptions[0];
  const yd = (byKey[yKey] && byKey[yKey].axisOk) ? byKey[yKey] : (axisOptions[1] || axisOptions[0]);
  const colorD = colorKey === NONE ? null : ((byKey[colorKey] && byKey[colorKey].colorOk) ? byKey[colorKey] : null);
  const sizeM = useMemo(
    () => measures.find((m) => m.key === sizeKey) || measures[0],
    [measures, sizeKey],
  );
  const xFellBack = !!(xd && xd.key !== xKey);
  const yFellBack = !!(yd && yd.key !== yKey);
  const colorFellBack = colorKey !== NONE && !colorD;

  const xInfo = useMemo(() => levelsFor(xd, rows), [xd, rows]);
  const yInfo = useMemo(() => levelsFor(yd, rows), [yd, rows]);

  const drawX = useMemo(() => (revX ? [...xInfo.levels].reverse() : xInfo.levels), [xInfo, revX]);
  const drawY = useMemo(() => (revY ? [...yInfo.levels].reverse() : yInfo.levels), [yInfo, revY]);

  const nx = drawX.length;
  const ny = drawY.length;
  const cellW = nx ? PLOT_W / nx : PLOT_W;
  const cellH = ny ? PLOT_H / ny : PLOT_H;

  const bx = Math.min(Math.max(splitX === null ? Math.max(0, Math.ceil(nx / 2) - 1) : splitX, 0), Math.max(0, nx - 2));
  const by = Math.min(Math.max(splitY === null ? Math.max(0, Math.ceil(ny / 2) - 1) : splitY, 0), Math.max(0, ny - 2));

  // ---- projection ----------------------------------------------------------
  const proj = useMemo(() => {
    const empty = {
      points: [], drawn: [], cells: [], cellByKey: new Map(), excluded: [],
      missX: 0, missY: 0, missBoth: 0, eligible: 0, total: rows.length,
      overflow: 0, sizeMissing: 0, colorTotals: [], colorMap: {}, quadCounts: {},
    };
    if (!xd || !yd || !nx || !ny) return empty;

    const xPos = {};
    drawX.forEach((v, i) => { xPos[v] = i; });
    const yPos = {};
    drawY.forEach((v, i) => { yPos[v] = i; });

    const points = [];
    const excluded = [];
    let missX = 0;
    let missY = 0;
    let missBoth = 0;
    const colorCounts = new Map();

    for (let i = 0; i < rows.length; i += 1) {
      const fs = rows[i];
      const xv = xd.get(fs);
      const yv = yd.get(fs);
      const noX = !xv;
      const noY = !yv;
      if (noX || noY) {
        if (noX) missX += 1;
        if (noY) missY += 1;
        if (noX && noY) missBoth += 1;
        const reasons = [];
        if (noX) reasons.push(xd.label);
        if (noY) reasons.push(yd.label);
        excluded.push({ id: fs.id, name: fs.name, type: fs.type, reasons });
        continue;
      }
      const cv = colorD ? (colorD.get(fs) || NOT_SET) : null;
      if (colorD) colorCounts.set(cv, (colorCounts.get(cv) || 0) + 1);
      points.push({
        id: fs.id,
        name: fs.name,
        xv,
        yv,
        xi: xPos[xv],
        yi: yPos[yv],
        colorValue: cv,
        measure: sizeM && sizeM.get ? sizeM.get(fs) : null,
        completion: fs.completion || 0,
      });
    }

    // cells: every distinct coordinate, with its true membership
    const cellMap = new Map();
    points.forEach((p) => {
      const k = `${p.xi}|${p.yi}`;
      let c = cellMap.get(k);
      if (!c) { c = { key: k, xi: p.xi, yi: p.yi, items: [] }; cellMap.set(k, c); }
      c.items.push(p);
    });
    const cells = [...cellMap.values()];

    // The cap counts DRAWN MARKS. In cluster mode a mark is a coordinate, so the
    // cap effectively never binds and every record is represented.
    const drawn = mode === 'jitter' ? points.slice(0, MAX_POINTS) : points;
    const overflow = mode === 'jitter' ? Math.max(0, points.length - drawn.length) : 0;

    const numericSize = sizeM && sizeM.key !== NONE && sizeM.key !== COUNT;
    const sizeMissing = numericSize ? points.filter((p) => p.measure === null).length : 0;

    cells.forEach((c) => {
      c.count = c.items.length;
      if (numericSize) {
        const vals = c.items.map((p) => p.measure).filter((v) => v !== null);
        c.sum = vals.reduce((s, v) => s + v, 0);
        c.withMeasure = vals.length;
      } else {
        c.sum = c.count;
        c.withMeasure = c.count;
      }
    });

    const colorTotals = [...colorCounts.entries()].map(([value, count]) => ({ value, count }));
    const order = colorD && colorD.order ? colorD.order : null;
    colorTotals.sort((a, b) => {
      const idx = (v) => {
        if (v === NOT_SET) return -1;
        const i = order ? order.indexOf(v) : -1;
        return i >= 0 ? i : 5000;
      };
      return (idx(a.value) - idx(b.value)) || (b.count - a.count) || a.value.localeCompare(b.value);
    });
    const colorMap = buildColorMap(colorD, colorTotals.map((c) => c.value));

    const quadCounts = { invest: 0, tolerate: 0, migrate: 0, eliminate: 0 };
    points.forEach((p) => {
      const right = p.xi > bx;
      const top = p.yi > by;
      const q = QUADRANTS.find((k) => k.right === right && k.top === top);
      if (q) quadCounts[q.key] += 1;
    });

    return {
      points,
      drawn,
      cells,
      cellByKey: cellMap,
      excluded,
      missX,
      missY,
      missBoth,
      eligible: points.length,
      total: rows.length,
      overflow,
      sizeMissing,
      numericSize,
      colorTotals,
      colorMap,
      quadCounts,
    };
  }, [rows, xd, yd, colorD, sizeM, mode, drawX, drawY, nx, ny, bx, by]);

  // radius scale over whatever the marks are measuring
  const radius = useMemo(() => {
    const clusterMode = mode === 'cluster';
    const values = clusterMode
      ? proj.cells.map((c) => (sizeM.key === NONE ? 1 : c.sum))
      : proj.drawn.map((p) => {
        if (sizeM.key === NONE) return 1;
        if (sizeM.key === COUNT) {
          const c = proj.cellByKey.get(`${p.xi}|${p.yi}`);
          return c ? c.count : 1;
        }
        return p.measure === null ? null : p.measure;
      });
    const finite = values.filter((v) => v !== null && Number.isFinite(v));
    const min = finite.length ? Math.min(...finite) : 0;
    const max = finite.length ? Math.max(...finite) : 0;
    const rMin = clusterMode ? 4 : 3;
    const rMax = clusterMode ? 19 : 12;
    const of = (v) => {
      if (v === null || !Number.isFinite(v)) return rMin * 0.8; // measure missing — smallest, and counted
      if (max <= min) return (rMin + rMax) / 2;
      return rMin + (rMax - rMin) * Math.sqrt((v - min) / (max - min));
    };
    return { of, min, max };
  }, [proj, sizeM, mode]);

  const cellOf = (xi, yi) => proj.cellByKey.get(`${xi}|${yi}`) || null;
  const selectedCell = cell ? cellOf(cell.xi, cell.yi) : null;

  // ---- radar ---------------------------------------------------------------
  const rowsById = useMemo(() => {
    const m = new Map();
    rows.forEach((fs) => m.set(fs.id, fs));
    return m;
  }, [rows]);

  const defaultRadarAxes = useMemo(() => {
    const wanted = ['functionalFit', 'technicalFit', 'businessCriticality', '__drTier', 'rto', 'rpo', 'lifecycleState'];
    const picked = wanted.filter((k) => byKey[k] && byKey[k].axisOk && byKey[k].hasOrder);
    if (picked.length >= 3) return picked.slice(0, 6);
    return orderedOptions.slice(0, Math.min(6, orderedOptions.length)).map((o) => o.key);
  }, [byKey, orderedOptions]);

  const radarAxes = useMemo(() => {
    const keys = (radarAxisKeys || defaultRadarAxes).filter((k) => byKey[k] && byKey[k].axisOk && byKey[k].hasOrder);
    return keys.slice(0, MAX_RADAR_AXES).map((k) => {
      const dim = byKey[k];
      const info = levelsFor(dim, rows);
      return { dim, levels: info.levels, extras: info.extras, orderSource: info.orderSource };
    }).filter((a) => a.levels.length >= 2);
  }, [radarAxisKeys, defaultRadarAxes, byKey, rows]);

  const groupDim = (byKey[groupKey] && byKey[groupKey].axisOk) ? byKey[groupKey] : orderedOptions[0] || axisOptions[0];

  const radar = useMemo(() => {
    const axes = radarAxes;
    const empty = { subjects: [], axes, coverage: [], universe: 0, note: '' };
    if (!axes.length) return empty;

    const norm = (axis, fs) => {
      const v = axis.dim.get(fs);
      if (!v) return null;
      const i = axis.levels.indexOf(v);
      if (i < 0) return null;
      return { raw: v, index: i, value: axis.levels.length > 1 ? i / (axis.levels.length - 1) : 0 };
    };

    let subjects = [];
    let universe = 0;

    if (radarMode === 'sheets') {
      const picked = pickedIds.map((id) => rowsById.get(id)).filter(Boolean).slice(0, MAX_RADAR_SUBJECTS);
      universe = picked.length;
      subjects = picked.map((fs, i) => ({
        key: fs.id,
        id: fs.id,
        label: fs.name,
        members: 1,
        color: PALETTE[i % PALETTE.length],
        values: axes.map((axis) => norm(axis, fs)),
      }));
    } else {
      const buckets = new Map();
      rows.forEach((fs) => {
        const g = groupDim ? (groupDim.get(fs) || NOT_SET) : NOT_SET;
        let b = buckets.get(g);
        if (!b) { b = { value: g, items: [] }; buckets.set(g, b); }
        b.items.push(fs);
      });
      const all = [...buckets.values()].sort((a, b) => b.items.length - a.items.length);
      universe = all.length;
      subjects = all.slice(0, MAX_RADAR_SUBJECTS).map((b, i) => ({
        key: b.value,
        id: null,
        label: b.value,
        members: b.items.length,
        color: b.value === NOT_SET ? NOT_SET_COLOR : PALETTE[i % PALETTE.length],
        values: axes.map((axis) => {
          const vals = b.items.map((fs) => norm(axis, fs)).filter(Boolean);
          if (!vals.length) return null;
          const mean = vals.reduce((s, v) => s + v.value, 0) / vals.length;
          return {
            raw: `mean of ${vals.length} of ${b.items.length}`,
            index: null,
            value: mean,
            n: vals.length,
            of: b.items.length,
          };
        }),
      }));
    }

    const coverage = axes.map((axis, ai) => {
      const withValue = subjects.filter((s) => s.values[ai]).length;
      return {
        label: axis.dim.label,
        withValue,
        total: subjects.length,
        levels: axis.levels.length,
        first: axis.levels[0],
        last: axis.levels[axis.levels.length - 1],
        scale: axis.dim.scale,
        extras: axis.extras.length,
      };
    });

    return { subjects, axes, coverage, universe };
  }, [radarAxes, radarMode, pickedIds, rowsById, rows, groupDim]);

  const nonScaleAxes = radar.axes.filter((a) => !a.dim.scale).map((a) => a.dim.label);

  // ---- narrative lines (shared by screen + exports) ------------------------
  const typeLabel = typeKey === ALL_TYPES ? 'fact sheets (all types)' : `${typeMeta(typeKey).label} fact sheets`;
  const basis = xd && yd
    ? `X: ${xd.label} · Y: ${yd.label}${sizeM && sizeM.key !== NONE ? ` · size: ${sizeM.label}` : ''}${colorD ? ` · colour: ${colorD.label}` : ''}`
    : '—';

  const excludedLine = (() => {
    if (!xd || !yd) return '';
    const parts = [];
    if (proj.missX) parts.push(`${num(proj.missX)} missing ${xd.label}`);
    if (proj.missY) parts.push(`${num(proj.missY)} missing ${yd.label}`);
    const k = proj.excluded.length;
    if (!k) return `${num(proj.eligible)} of ${num(proj.total)} ${typeLabel} plotted; 0 excluded — every record has both values.`;
    return `${num(proj.eligible)} of ${num(proj.total)} ${typeLabel} plotted; ${num(k)} excluded (${parts.join(', ')}${proj.missBoth ? `; ${num(proj.missBoth)} missing both` : ''}).`;
  })();

  const capLine = proj.overflow
    ? `${num(proj.drawn.length)} of ${num(proj.eligible)} plotted records are drawn as marks; ${num(proj.overflow)} are counted everywhere on this page but not drawn (cap ${num(MAX_POINTS)} marks). Switch to "one bubble per coordinate" to represent all of them.`
    : '';

  const quadLine = (nx > 1 && ny > 1)
    ? `Quadrant split — vertical line between “${drawX[bx]}” and “${drawX[bx + 1]}”; horizontal line between “${drawY[by]}” and “${drawY[by + 1]}”.`
    : 'Quadrant split unavailable — an axis has fewer than two values.';

  // ---- exports -------------------------------------------------------------
  const portfolioCsv = () => {
    if (!xd || !yd) return;
    const cols = [
      { label: 'Fact sheet', value: 'name' },
      { label: 'Type', value: 'type' },
      { label: 'Status', value: 'status' },
      { label: `X — ${xd.label}`, value: 'x' },
      { label: `Y — ${yd.label}`, value: 'y' },
      { label: `Size — ${sizeM.label}`, value: 'size' },
      { label: `Colour — ${colorD ? colorD.label : '(none)'}`, value: 'color' },
      { label: 'Records at this coordinate', value: 'atCoord' },
      { label: 'Quadrant', value: 'quadrant' },
    ];
    const out = [
      {
        name: '(basis)',
        type: typeLabel,
        status: basis,
        x: `axis order: ${xInfo.orderSource}`,
        y: `axis order: ${yInfo.orderSource}`,
        size: mode === 'cluster' ? 'one bubble per coordinate' : 'one mark per record (jittered)',
        color: new Date().toISOString(),
        atCoord: '',
        quadrant: showQuadrants ? quadLine : 'quadrants off',
      },
      {
        name: '(counts)',
        type: '',
        status: excludedLine + (capLine ? ` ${capLine}` : ''),
        x: `${num(proj.missX)} missing ${xd.label}`,
        y: `${num(proj.missY)} missing ${yd.label}`,
        size: proj.numericSize ? `${num(proj.sizeMissing)} plotted records have no ${sizeM.label}` : '',
        color: '',
        atCoord: '',
        quadrant: '',
      },
    ];
    const drawnIds = new Set(proj.drawn.map((p) => p.id));
    proj.points.forEach((p) => {
      const c = cellOf(p.xi, p.yi);
      const q = QUADRANTS.find((k) => k.right === (p.xi > bx) && k.top === (p.yi > by));
      out.push({
        name: p.name,
        type: typeKey === ALL_TYPES ? (rowsById.get(p.id) || {}).type || '' : typeKey,
        status: drawnIds.has(p.id) || mode === 'cluster' ? 'plotted' : 'plotted (beyond mark cap — not drawn)',
        x: p.xv,
        y: p.yv,
        size: sizeM.key === COUNT ? (c ? c.count : 1) : (p.measure === null ? '' : p.measure),
        color: p.colorValue === null ? '' : p.colorValue,
        atCoord: c ? c.count : 1,
        quadrant: showQuadrants && nx > 1 && ny > 1 ? (q ? q.label : '') : '',
      });
    });
    proj.excluded.forEach((e) => {
      out.push({
        name: e.name,
        type: e.type,
        status: `EXCLUDED — no value for ${e.reasons.join(' and ')}`,
        x: '',
        y: '',
        size: '',
        color: '',
        atCoord: '',
        quadrant: '',
      });
    });
    exportCsv(safeName(`portfolio-${xd.key}-vs-${yd.key}.csv`), out, cols);
  };

  const radarCsv = () => {
    const cols = [
      { label: 'Subject', value: 'subject' },
      { label: 'Records in subject', value: 'members' },
      { label: 'Axis', value: 'axis' },
      { label: 'Axis order', value: 'order' },
      { label: 'Value', value: 'value' },
      { label: 'Picklist position', value: 'position' },
      { label: 'Normalised 0–1', value: 'normalised' },
    ];
    const out = [{
      subject: '(basis)',
      members: radar.subjects.length,
      axis: radar.axes.map((a) => a.dim.label).join(' | '),
      order: 'each axis normalised to position-in-picklist / (levels - 1)',
      value: radarMode === 'sheets' ? 'hand-picked fact sheets' : `groups of ${groupDim ? groupDim.label : ''}`,
      position: nonScaleAxes.length ? `NOT A SCALE: ${nonScaleAxes.join(', ')}` : 'all axes are ordered scales',
      normalised: new Date().toISOString(),
    }];
    radar.subjects.forEach((s) => {
      radar.axes.forEach((axis, ai) => {
        const v = s.values[ai];
        out.push({
          subject: s.label,
          members: s.members,
          axis: axis.dim.label,
          order: `${axis.levels[0]} → ${axis.levels[axis.levels.length - 1]} (${axis.levels.length} levels, ${axis.orderSource})`,
          value: v ? v.raw : 'MISSING — not plotted',
          position: v && v.index !== null && v.index !== undefined ? `${v.index} of ${axis.levels.length - 1}` : '',
          normalised: v ? Math.round(v.value * 1000) / 1000 : '',
        });
      });
    });
    exportCsv(safeName(`radar-${radarMode}-${radar.axes.length}-axes.csv`), out, cols);
  };

  // ---- menus ---------------------------------------------------------------
  // Flat MenuItems only: this MUI version clones every Select child with
  // role="option", so a ListSubheader renders as a selectable undefined option.
  const dimItems = (list, { requireOrder } = {}) => list.map((d) => (
    <MenuItem key={d.key} value={d.key} disabled={!!requireOrder && !d.hasOrder} sx={{ fontSize: 13 }}>
      {d.label}
      <span style={{ color: '#9AA6B2', fontSize: 11, marginLeft: 8 }}>
        {d.section}
        {d.note ? ` · ${d.note}` : ''}
      </span>
    </MenuItem>
  ));

  const unavailableNote = `${axisOptions.length} of ${options.length} schema dimensions can be an axis in this workspace`
    + ` (a field needs a bounded set of at most ${AXIS_MAX_LEVELS} values, and at least one record must use it).`;

  // ---- render helpers ------------------------------------------------------
  const emptyState = (title, msg) => (
    <Paper sx={{ p: 6, textAlign: 'center', color: GREY }}>
      <Typography sx={{ fontWeight: 700, color: HEADER, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2">{msg}</Typography>
    </Paper>
  );

  const countChips = (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
      <Chip size="small" label={`${num(proj.eligible)} of ${num(proj.total)} plotted`} sx={{ height: 22, bgcolor: '#e8f0fe', fontWeight: 700 }} />
      <Chip
        size="small"
        label={`${num(proj.excluded.length)} excluded for missing values`}
        onClick={proj.excluded.length ? () => setShowExcluded((v) => !v) : undefined}
        sx={{ height: 22, bgcolor: proj.excluded.length ? '#fdecea' : '#eef2f6', color: proj.excluded.length ? '#8a2118' : GREY, fontWeight: 700 }}
      />
      <Chip size="small" label={`${num(proj.cells.length)} distinct coordinates`} sx={{ height: 22 }} />
      {!!proj.overflow && (
        <Chip size="small" label={`${num(proj.overflow)} plotted but not drawn (mark cap)`} sx={{ height: 22, bgcolor: '#fff4e5', color: '#8a5300' }} />
      )}
      {!!proj.sizeMissing && (
        <Chip size="small" label={`${num(proj.sizeMissing)} plotted records have no ${sizeM.label} — drawn at the minimum size`} sx={{ height: 22, bgcolor: '#fff4e5', color: '#8a5300' }} />
      )}
    </Stack>
  );

  // ---- scatter SVG ---------------------------------------------------------
  const renderScatter = () => {
    const xAt = (i) => PAD_L + (i + 0.5) * cellW;
    const yAt = (i) => PAD_T + PLOT_H - (i + 0.5) * cellH;
    const splitPxX = PAD_L + (bx + 1) * cellW;
    const splitPxY = PAD_T + PLOT_H - (by + 1) * cellH;
    const quads = showQuadrants && nx > 1 && ny > 1;

    return (
      <svg
        ref={scatterRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block', maxHeight: 720, background: '#ffffff' }}
      >
        <rect x={0} y={0} width={W} height={H} fill="#ffffff" />

        {/* quadrant bands, drawn under everything */}
        {quads && QUADRANTS.map((q) => {
          const x0 = q.right ? splitPxX : PAD_L;
          const w = q.right ? (PAD_L + PLOT_W) - splitPxX : splitPxX - PAD_L;
          const y0 = q.top ? PAD_T : splitPxY;
          const h = q.top ? splitPxY - PAD_T : (PAD_T + PLOT_H) - splitPxY;
          return (
            <g key={q.key}>
              <rect x={x0} y={y0} width={Math.max(0, w)} height={Math.max(0, h)} fill={q.color} opacity={0.055} />
              <text
                x={q.right ? x0 + Math.max(0, w) - 8 : x0 + 8}
                y={q.top ? y0 + 16 : y0 + Math.max(0, h) - 6}
                textAnchor={q.right ? 'end' : 'start'}
                fontSize={13}
                fontWeight={700}
                fill={q.color}
                opacity={0.85}
              >
                {`${q.label} — ${num(proj.quadCounts[q.key] || 0)}`}
              </text>
            </g>
          );
        })}

        {/* cell grid */}
        {drawX.map((xv, i) => drawY.map((yv, j) => (
          <rect
            key={`g-${i}-${j}`}
            x={PAD_L + i * cellW}
            y={PAD_T + PLOT_H - (j + 1) * cellH}
            width={cellW}
            height={cellH}
            fill="none"
            stroke="#eef2f6"
            strokeWidth={1}
          />
        )))}

        {/* per-cell true count — the guard against ten apps reading as one dot */}
        {proj.cells.map((c) => (
          <text
            key={`c-${c.key}`}
            x={PAD_L + (c.xi + 1) * cellW - 5}
            y={PAD_T + PLOT_H - (c.yi + 1) * cellH + 13}
            textAnchor="end"
            fontSize={10}
            fill="#8b98a5"
          >
            {num(c.count)}
          </text>
        ))}

        {/* axis frame */}
        <rect x={PAD_L} y={PAD_T} width={PLOT_W} height={PLOT_H} fill="none" stroke="#d7dee6" strokeWidth={1} />

        {quads && (
          <g>
            <line x1={splitPxX} y1={PAD_T} x2={splitPxX} y2={PAD_T + PLOT_H} stroke={NAVY} strokeWidth={1.5} strokeDasharray="6 4" />
            <line x1={PAD_L} y1={splitPxY} x2={PAD_L + PLOT_W} y2={splitPxY} stroke={NAVY} strokeWidth={1.5} strokeDasharray="6 4" />
          </g>
        )}

        {/* X labels */}
        {drawX.map((v, i) => (
          <text
            key={`xl-${v}`}
            x={xAt(i)}
            y={PAD_T + PLOT_H + 16}
            fontSize={11}
            fill="#33414f"
            textAnchor="end"
            transform={`rotate(-32 ${xAt(i).toFixed(1)} ${(PAD_T + PLOT_H + 16).toFixed(1)})`}
          >
            {trunc(v, 26)}
          </text>
        ))}
        {/* Y labels */}
        {drawY.map((v, i) => (
          <text key={`yl-${v}`} x={PAD_L - 10} y={yAt(i) + 4} fontSize={11} fill="#33414f" textAnchor="end">
            {trunc(v, 24)}
          </text>
        ))}

        <text x={PAD_L + PLOT_W / 2} y={H - 10} textAnchor="middle" fontSize={12} fontWeight={700} fill={NAVY}>
          {`${xd ? xd.label : ''}${revX ? ' (axis reversed)' : ''} →`}
        </text>
        <text
          x={16}
          y={PAD_T + PLOT_H / 2}
          textAnchor="middle"
          fontSize={12}
          fontWeight={700}
          fill={NAVY}
          transform={`rotate(-90 16 ${(PAD_T + PLOT_H / 2).toFixed(1)})`}
        >
          {`${yd ? yd.label : ''}${revY ? ' (axis reversed)' : ''} →`}
        </text>

        {/* marks */}
        {mode === 'cluster'
          ? proj.cells.map((c) => {
            const r = radius.of(sizeM.key === NONE ? 1 : c.sum);
            const sel = cell && cell.xi === c.xi && cell.yi === c.yi;
            const cv = c.items.length && colorD
              ? (c.items.reduce((acc, p) => {
                acc.map[p.colorValue] = (acc.map[p.colorValue] || 0) + 1;
                if (acc.map[p.colorValue] > acc.best) { acc.best = acc.map[p.colorValue]; acc.value = p.colorValue; }
                return acc;
              }, { map: {}, best: 0, value: NOT_SET }).value)
              : null;
            return (
              <g key={`cl-${c.key}`} style={{ cursor: 'pointer' }} onClick={() => setCell(sel ? null : { xi: c.xi, yi: c.yi })}>
                <title>
                  {`${drawX[c.xi]} · ${drawY[c.yi]} — ${c.count} record${c.count === 1 ? '' : 's'}`
                    + `${proj.numericSize ? ` · ${sizeM.label} total ${num(Math.round(c.sum))} across ${c.withMeasure} of ${c.count}` : ''}`
                    + `${cv ? ` · most common ${colorD.label}: ${cv}` : ''}`}
                </title>
                <circle
                  cx={xAt(c.xi)}
                  cy={yAt(c.yi)}
                  r={r}
                  fill={colorD ? (proj.colorMap[cv] || UNKNOWN_COLOR) : BLUE}
                  fillOpacity={0.75}
                  stroke={sel ? NAVY : '#ffffff'}
                  strokeWidth={sel ? 2.5 : 1}
                />
                {r >= 9 && (
                  <text x={xAt(c.xi)} y={yAt(c.yi) + 4} textAnchor="middle" fontSize={Math.min(12, r)} fontWeight={700} fill="#ffffff">
                    {num(c.count)}
                  </text>
                )}
              </g>
            );
          })
          : proj.drawn.map((p) => {
            const c = cellOf(p.xi, p.yi);
            const jx = (hashUnit(`${p.id}|x`) - 0.5) * cellW * 0.72;
            const jy = (hashUnit(`${p.id}|y`) - 0.5) * cellH * 0.72;
            const v = sizeM.key === NONE ? 1 : (sizeM.key === COUNT ? (c ? c.count : 1) : p.measure);
            return (
              <circle
                key={p.id}
                cx={xAt(p.xi) + jx}
                cy={yAt(p.yi) + jy}
                r={radius.of(v)}
                fill={colorD ? (proj.colorMap[p.colorValue] || UNKNOWN_COLOR) : BLUE}
                fillOpacity={0.8}
                stroke={p.colorValue === NOT_SET ? '#5C6B7A' : '#ffffff'}
                strokeWidth={p.colorValue === NOT_SET ? 1 : 0.8}
                style={{ cursor: 'pointer' }}
                onClick={() => onOpen && onOpen(p.id)}
              >
                <title>
                  {`${p.name} — ${xd.label}: ${p.xv} · ${yd.label}: ${p.yv}`
                    + `${colorD ? ` · ${colorD.label}: ${p.colorValue}` : ''}`
                    + `${proj.numericSize ? ` · ${sizeM.label}: ${p.measure === null ? 'not captured' : num(p.measure)}` : ''}`
                    + `${c ? ` · ${c.count} record${c.count === 1 ? '' : 's'} share this coordinate` : ''}`}
                </title>
              </circle>
            );
          })}
      </svg>
    );
  };

  // ---- radar SVG -----------------------------------------------------------
  const RW = 720;
  const RCX = RW / 2;
  const RCY = 330;
  const RR = 232;

  const renderRadar = () => {
    const k = radar.axes.length;
    const angle = (i) => (-Math.PI / 2) + (i * 2 * Math.PI) / k;
    const at = (i, t) => ({
      x: RCX + RR * t * Math.cos(angle(i)),
      y: RCY + RR * t * Math.sin(angle(i)),
    });

    return (
      <svg ref={radarRef} viewBox={`0 0 ${RW} 700`} width="100%" style={{ display: 'block', maxHeight: 720, background: '#ffffff' }}>
        <rect x={0} y={0} width={RW} height={700} fill="#ffffff" />

        {[0.25, 0.5, 0.75, 1].map((t) => (
          <polygon
            key={`ring-${t}`}
            points={radar.axes.map((a, i) => { const p = at(i, t); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(' ')}
            fill="none"
            stroke="#e6ebf1"
            strokeWidth={1}
          />
        ))}

        {radar.axes.map((a, i) => {
          const end = at(i, 1);
          const lab = at(i, 1.1);
          const anchor = Math.abs(lab.x - RCX) < 12 ? 'middle' : (lab.x < RCX ? 'end' : 'start');
          return (
            <g key={`axis-${a.dim.key}`}>
              <line x1={RCX} y1={RCY} x2={end.x} y2={end.y} stroke="#cfd8e2" strokeWidth={1} />
              <text x={lab.x} y={lab.y} fontSize={11.5} fontWeight={700} fill={NAVY} textAnchor={anchor}>
                {trunc(a.dim.label, 28)}
              </text>
              <text x={lab.x} y={lab.y + 13} fontSize={10} fill={GREY} textAnchor={anchor}>
                {`${trunc(a.levels[a.levels.length - 1], 20)}${a.dim.scale ? '' : ' ⚠ not a scale'}`}
              </text>
            </g>
          );
        })}

        {radar.subjects.map((s) => {
          const pts = s.values.map((v, i) => (v ? at(i, v.value) : null));
          const present = pts.filter(Boolean);
          if (!present.length) return null;
          // Vertices only where a value exists; the polygon is closed across the
          // gaps with a dashed edge so a missing axis is visible as a gap rather
          // than silently drawn at zero.
          const ring = pts.map((p, i) => ({ p, i })).filter((o) => o.p);
          const segs = ring.map((o, n) => {
            const nxt = ring[(n + 1) % ring.length];
            const skipped = ((nxt.i - o.i + radar.axes.length) % radar.axes.length) > 1;
            return { a: o.p, b: nxt.p, skipped };
          });
          return (
            <g key={`sub-${s.key}`}>
              {present.length >= 3 && (
                <polygon
                  points={present.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
                  fill={s.color}
                  fillOpacity={0.1}
                  stroke="none"
                />
              )}
              {segs.map((sg, n) => (
                <line
                  key={`seg-${s.key}-${n}`}
                  x1={sg.a.x}
                  y1={sg.a.y}
                  x2={sg.b.x}
                  y2={sg.b.y}
                  stroke={s.color}
                  strokeWidth={sg.skipped ? 1.4 : 2.2}
                  strokeDasharray={sg.skipped ? '5 4' : undefined}
                  strokeOpacity={sg.skipped ? 0.55 : 0.95}
                />
              ))}
              {pts.map((p, i) => (p ? (
                <circle
                  key={`pt-${s.key}-${i}`}
                  cx={p.x}
                  cy={p.y}
                  r={4}
                  fill={s.color}
                  stroke="#ffffff"
                  strokeWidth={1}
                  style={{ cursor: s.id ? 'pointer' : 'default' }}
                  onClick={() => (s.id && onOpen ? onOpen(s.id) : null)}
                >
                  <title>{`${s.label} · ${radar.axes[i].dim.label}: ${s.values[i].raw}`}</title>
                </circle>
              ) : null))}
            </g>
          );
        })}

        <circle cx={RCX} cy={RCY} r={2.5} fill="#b6c1cc" />
        <text x={RCX} y={686} textAnchor="middle" fontSize={11} fill={GREY}>
          Centre = first value of each picklist · outer ring = last value. Dashed edges bridge an axis where the subject has no value.
        </text>
      </svg>
    );
  };

  // ---- render --------------------------------------------------------------
  const showPortfolio = tab === 0;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Portfolio &amp; Radar</Typography>
        <TextField
          select size="small" label="Fact sheet type" value={typeKey}
          onChange={(e) => setTypeKey(e.target.value)} sx={{ minWidth: 170 }}
        >
          <MenuItem value={ALL_TYPES}>All types</MenuItem>
          {FACT_SHEET_TYPES.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={showPortfolio ? portfolioCsv : radarCsv}
          disabled={showPortfolio ? !proj.total : !radar.subjects.length}
        >
          CSV
        </Button>
        <Button
          startIcon={<ImageIcon />}
          variant="outlined"
          onClick={() => (showPortfolio
            ? svgToPng(scatterRef.current, safeName(`portfolio-${xd ? xd.key : 'x'}-vs-${yd ? yd.key : 'y'}.png`))
            : svgToPng(radarRef.current, safeName(`radar-${radarMode}.png`)))}
          disabled={showPortfolio ? !proj.eligible : !radar.subjects.length}
        >
          PNG
        </Button>
        <Button
          startIcon={<PictureAsPdfIcon />}
          variant="outlined"
          onClick={() => (showPortfolio
            ? printElementToPdf('portfolio-print', `Application Portfolio — ${basis}`)
            : printElementToPdf('radar-print', 'Portfolio Radar'))}
          disabled={showPortfolio ? !proj.eligible : !radar.subjects.length}
        >
          PDF
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e6eaee' }}>
        <Tab label="Portfolio" sx={{ fontWeight: 700 }} />
        <Tab label="Radar" sx={{ fontWeight: 700 }} />
      </Tabs>

      {loading && <Paper sx={{ p: 4, textAlign: 'center', color: GREY }}>Loading fact sheets…</Paper>}

      {!loading && !!error && (
        <Paper sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 700, color: '#8a2118', mb: 0.5 }}>Could not load fact sheets</Typography>
          <Typography variant="body2" sx={{ color: GREY }}>
            {error} — nothing is plotted, so this screen is showing no estate at all rather than a partial one.
          </Typography>
        </Paper>
      )}

      {/* ================= PORTFOLIO ================= */}
      {!loading && !error && showPortfolio && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <TextField
              select size="small" label="X axis" value={xd ? xd.key : ''}
              onChange={(e) => setXKey(e.target.value)} sx={{ minWidth: 250 }}
              SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 460 } } } }}
            >
              {dimItems(axisOptions)}
            </TextField>
            <TextField
              select size="small" label="Y axis" value={yd ? yd.key : ''}
              onChange={(e) => setYKey(e.target.value)} sx={{ minWidth: 250 }}
              SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 460 } } } }}
            >
              {dimItems(axisOptions)}
            </TextField>
            <TextField
              select size="small" label="Bubble size" value={sizeM ? sizeM.key : NONE}
              onChange={(e) => setSizeKey(e.target.value)} sx={{ minWidth: 230 }}
              SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 460 } } } }}
            >
              {measures.map((m) => (
                <MenuItem key={m.key} value={m.key} sx={{ fontSize: 13 }}>
                  {m.label}
                  <span style={{ color: '#9AA6B2', fontSize: 11, marginLeft: 8 }}>{m.section}</span>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" label="Colour by" value={colorD ? colorD.key : NONE}
              onChange={(e) => setColorKey(e.target.value)} sx={{ minWidth: 230 }}
              SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 460 } } } }}
            >
              <MenuItem value={NONE} sx={{ fontSize: 13 }}>No colour dimension</MenuItem>
              {dimItems(colorOptions)}
            </TextField>
            <TextField
              select size="small" label="Overlapping records" value={mode}
              onChange={(e) => setMode(e.target.value)} sx={{ minWidth: 250 }}
            >
              <MenuItem value="jitter" sx={{ fontSize: 13 }}>One mark per record (jittered in cell)</MenuItem>
              <MenuItem value="cluster" sx={{ fontSize: 13 }}>One bubble per coordinate (sized by count)</MenuItem>
            </TextField>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={revX} onChange={(e) => setRevX(e.target.checked)} />}
              label={<Typography variant="caption">Reverse X</Typography>}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={revY} onChange={(e) => setRevY(e.target.checked)} />}
              label={<Typography variant="caption">Reverse Y</Typography>}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={showQuadrants} onChange={(e) => setShowQuadrants(e.target.checked)} />}
              label={<Typography variant="caption">Quadrant labelling</Typography>}
            />
            <TextField
              select size="small" label="X split after" value={nx > 1 ? String(bx) : ''} disabled={!showQuadrants || nx < 2}
              onChange={(e) => setSplitX(Number(e.target.value))} sx={{ minWidth: 210 }}
            >
              {drawX.slice(0, Math.max(0, nx - 1)).map((v, i) => (
                <MenuItem key={`sx-${v}`} value={String(i)} sx={{ fontSize: 13 }}>{trunc(v, 28)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" label="Y split after" value={ny > 1 ? String(by) : ''} disabled={!showQuadrants || ny < 2}
              onChange={(e) => setSplitY(Number(e.target.value))} sx={{ minWidth: 210 }}
            >
              {drawY.slice(0, Math.max(0, ny - 1)).map((v, i) => (
                <MenuItem key={`sy-${v}`} value={String(i)} sx={{ fontSize: 13 }}>{trunc(v, 28)}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1.5 }}>
            {unavailableNote}
            {xFellBack && ` Requested X dimension is unavailable here — showing ${xd.label}.`}
            {yFellBack && ` Requested Y dimension is unavailable here — showing ${yd.label}.`}
            {colorFellBack && ' Requested colour dimension is unavailable here — colour is off.'}
          </Typography>

          {!proj.total && emptyState('Nothing to plot yet', `No ${typeLabel} in this workspace. Import or create some to build the portfolio.`)}

          {!!proj.total && (!xd || !yd) && emptyState(
            'No chartable axis',
            'No field in this workspace holds a bounded set of values yet, so there is nothing to put on an axis.',
          )}

          {!!proj.total && !!xd && !!yd && (
            <div id="portfolio-print">
              <Paper sx={{ p: 1.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>
                  Application Portfolio — {basis}
                </Typography>
                {countChips}
                <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                  {excludedLine}
                </Typography>
                {!!capLine && (
                  <Typography variant="caption" sx={{ color: '#8a5300', display: 'block' }}>{capLine}</Typography>
                )}
                <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                  X axis order: {xInfo.orderSource} ({nx} value{nx === 1 ? '' : 's'}
                  {xInfo.extras.length ? `, ${xInfo.extras.length} outside the picklist appended at the end` : ''}).
                  &nbsp;Y axis order: {yInfo.orderSource} ({ny} value{ny === 1 ? '' : 's'}
                  {yInfo.extras.length ? `, ${yInfo.extras.length} outside the picklist appended at the end` : ''}).
                  {(!xd.scale || !yd.scale) && ' ⚠ At least one axis is an ordered set rather than a scale — distance along it carries no magnitude.'}
                </Typography>
                {showQuadrants && (
                  <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                    {quadLine} Quadrant names read X low→high left→right and Y low→high bottom→top (the TIME reading of
                    functional fit vs technical fit). Reverse an axis or turn the labelling off when that reading does not apply.
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                  {mode === 'jitter'
                    ? 'Each record is one mark, offset deterministically inside its cell so coincident records stay countable; the exact number of records in each cell is printed in the cell corner.'
                    : 'Each bubble is one coordinate; the number inside it (and in the cell corner) is the exact number of records there. Click a bubble to list them.'}
                </Typography>
              </Paper>

              <Paper sx={{ p: 1, overflowX: 'auto' }}>
                {renderScatter()}
              </Paper>

              {!!proj.colorTotals.length && (
                <Paper sx={{ p: 1.25, mt: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 13, mb: 0.5 }}>
                    Colour — {colorD.label}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {proj.colorTotals.slice(0, MAX_LEGEND).map((c) => (
                      <Box
                        key={c.value}
                        style={{
                          backgroundColor: proj.colorMap[c.value],
                          color: c.value === NOT_SET ? '#1F2A36' : '#fff',
                          WebkitPrintColorAdjust: 'exact',
                          printColorAdjust: 'exact',
                        }}
                        sx={{
                          px: 0.75, py: 0.25, borderRadius: 3, fontSize: 11, fontWeight: 600,
                          border: c.value === NOT_SET ? '1px dashed #5C6B7A' : '1px solid transparent',
                        }}
                      >
                        {c.value} ({num(c.count)})
                      </Box>
                    ))}
                    {proj.colorTotals.length > MAX_LEGEND && (
                      <Typography variant="caption" sx={{ color: GREY }}>
                        +{proj.colorTotals.length - MAX_LEGEND} more {colorD.label} values
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5 }}>
                    “{NOT_SET}” is a colour of its own — a plotted record with no {colorD.label} keeps its position and is never
                    folded into a real value.
                  </Typography>
                </Paper>
              )}

              {!!selectedCell && (
                <Paper sx={{ p: 1.5, mt: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>
                      {xd.label}: {drawX[selectedCell.xi]} · {yd.label}: {drawY[selectedCell.yi]}
                    </Typography>
                    <Chip size="small" label={`${num(selectedCell.count)} record${selectedCell.count === 1 ? '' : 's'}`} sx={{ height: 20 }} />
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selectedCell.items.slice(0, 300).map((p) => (
                      <Box
                        key={p.id}
                        onClick={() => onOpen && onOpen(p.id)}
                        title={`${p.name} · ${p.completion}% complete`}
                        sx={{
                          bgcolor: BLUE, color: '#fff', px: 1, py: 0.5, borderRadius: 0.5, fontSize: 11,
                          fontWeight: 600, cursor: 'pointer', maxWidth: 220, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { bgcolor: NAVY },
                        }}
                      >
                        {p.name}
                      </Box>
                    ))}
                  </Box>
                  {selectedCell.count > 300 && (
                    <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                      +{num(selectedCell.count - 300)} more at this coordinate (counted above and in the CSV).
                    </Typography>
                  )}
                </Paper>
              )}

              <Paper sx={{ p: 1.5, mt: 1.5, bgcolor: proj.excluded.length ? '#fffaf7' : '#fafbfc' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 13 }}>
                    Excluded for missing values — {num(proj.excluded.length)} of {num(proj.total)} ({pct(proj.excluded.length, proj.total)}%)
                  </Typography>
                  {!!proj.excluded.length && (
                    <Button size="small" onClick={() => setShowExcluded((v) => !v)}>
                      {showExcluded ? 'Hide list' : 'Show list'}
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                  A record with no value for an axis has no position on it, so it is left off the chart rather than placed
                  somewhere convenient. Every one of them is named here and in the CSV — during discovery this set is usually the finding.
                </Typography>
                <Collapse in={showExcluded && !!proj.excluded.length}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {proj.excluded.slice(0, MAX_EXCLUDED_LISTED).map((e) => (
                      <Chip
                        key={e.id}
                        size="small"
                        label={`${trunc(e.name, 40)} — no ${e.reasons.join(' / ')}`}
                        onClick={() => onOpen && onOpen(e.id)}
                        sx={{ height: 20, fontSize: 11, bgcolor: '#fdecea', color: '#8a2118' }}
                      />
                    ))}
                  </Box>
                  {proj.excluded.length > MAX_EXCLUDED_LISTED && (
                    <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5 }}>
                      +{num(proj.excluded.length - MAX_EXCLUDED_LISTED)} more listed in the CSV export.
                    </Typography>
                  )}
                </Collapse>
              </Paper>
            </div>
          )}
        </Box>
      )}

      {/* ================= RADAR ================= */}
      {!loading && !error && !showPortfolio && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <TextField
              select size="small" label="Compare" value={radarMode}
              onChange={(e) => setRadarMode(e.target.value)} sx={{ minWidth: 220 }}
            >
              <MenuItem value="sheets" sx={{ fontSize: 13 }}>Hand-picked fact sheets</MenuItem>
              <MenuItem value="groups" sx={{ fontSize: 13 }}>Groups (mean position)</MenuItem>
            </TextField>

            {radarMode === 'groups' && (
              <TextField
                select size="small" label="Group by" value={groupDim ? groupDim.key : ''}
                onChange={(e) => setGroupKey(e.target.value)} sx={{ minWidth: 250 }}
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 460 } } } }}
              >
                {dimItems(axisOptions)}
              </TextField>
            )}

            <TextField
              select size="small" label={`Axes (${radar.axes.length} of max ${MAX_RADAR_AXES})`}
              value={radarAxes.map((a) => a.dim.key)}
              onChange={(e) => setRadarAxisKeys(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
              )}
              sx={{ minWidth: 320 }}
              SelectProps={{
                multiple: true,
                renderValue: (sel) => sel.map((k) => (byKey[k] ? byKey[k].label : k)).join(', '),
                MenuProps: { PaperProps: { style: { maxHeight: 460 } } },
              }}
            >
              {dimItems(axisOptions, { requireOrder: true })}
            </TextField>

            <FormControlLabel
              control={<Checkbox size="small" checked={showRadarGaps} onChange={(e) => setShowRadarGaps(e.target.checked)} />}
              label={<Typography variant="caption">Show per-subject gaps</Typography>}
            />
          </Stack>

          <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1.5 }}>
            {orderedOptions.length} of {options.length} schema dimensions can be a radar axis in this workspace. A dimension
            whose values have no defined order is listed but disabled — putting it on a radius would invent an ordering the
            schema never states.
            {radarMode === 'groups' && groupDim && groupDim.key !== groupKey
              && ` Requested group-by dimension is unavailable here — grouping by ${groupDim.label}.`}
          </Typography>

          {radarMode === 'sheets' && (
            <Autocomplete
              multiple
              size="small"
              options={rows}
              value={pickedIds.map((id) => rowsById.get(id)).filter(Boolean)}
              onChange={(e, v) => setPickedIds(v.slice(0, MAX_RADAR_SUBJECTS).map((o) => o.id))}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionDisabled={() => pickedIds.length >= MAX_RADAR_SUBJECTS}
              filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                const hit = q ? opts.filter((o) => String(o.name || '').toLowerCase().indexOf(q) !== -1) : opts;
                return hit.slice(0, 40);
              }}
              sx={{ mb: 2, maxWidth: 900 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Fact sheets to compare (up to ${MAX_RADAR_SUBJECTS})`}
                  placeholder={pickedIds.length >= MAX_RADAR_SUBJECTS ? 'Maximum reached' : 'Search by name…'}
                  helperText={`${pickedIds.length} of ${MAX_RADAR_SUBJECTS} selected · ${num(rows.length)} ${typeLabel} available (search shows the first 40 matches)`}
                />
              )}
            />
          )}

          {!rows.length && emptyState('Nothing to compare yet', `No ${typeLabel} in this workspace.`)}

          {!!rows.length && radar.axes.length < 3 && emptyState(
            'Pick at least three ordered axes',
            'A radar needs three or more dimensions whose values have a defined order. Dimensions with no defined order are'
            + ' listed but disabled: putting them on a radius would invent an ordering the schema never states.',
          )}

          {!!rows.length && radar.axes.length >= 3 && !radar.subjects.length && emptyState(
            'Pick something to compare',
            'Choose up to eight fact sheets, or switch to group mode to compare the largest groups of a dimension.',
          )}

          {!!rows.length && radar.axes.length >= 3 && !!radar.subjects.length && (
            <div id="radar-print">
              <Paper sx={{ p: 1.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>
                  Portfolio Radar — {radar.subjects.length} {radarMode === 'sheets' ? 'fact sheet' : 'group'}
                  {radar.subjects.length === 1 ? '' : 's'} across {radar.axes.length} axes
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label={`${num(rows.length)} ${typeLabel} in scope`} sx={{ height: 22 }} />
                  {radarMode === 'groups' && (
                    <Chip
                      size="small"
                      label={`${radar.subjects.length} of ${num(radar.universe)} ${groupDim ? groupDim.label : ''} groups shown (largest first)`}
                      sx={{ height: 22, bgcolor: radar.universe > radar.subjects.length ? '#fff4e5' : '#e8f0fe', color: radar.universe > radar.subjects.length ? '#8a5300' : NAVY, fontWeight: 700 }}
                    />
                  )}
                  {radarMode === 'sheets' && (
                    <Chip size="small" label={`${radar.subjects.length} of ${num(rows.length)} records selected`} sx={{ height: 22, bgcolor: '#e8f0fe', fontWeight: 700 }} />
                  )}
                </Stack>
                <Typography variant="caption" sx={{ color: GREY, display: 'block' }}>
                  <strong>How each axis is normalised:</strong> a value is placed at its position in the field&rsquo;s own picklist,
                  divided by (number of picklist values &minus; 1). The centre is the first picklist value and the outer ring is
                  the last. The picklists differ in length and in meaning, so <strong>a step on one axis is not equivalent to a
                  step on another, and the area of a shape carries no units</strong>. Read each spoke on its own; the polygon is a
                  reading aid, not a score.
                </Typography>
                {!!nonScaleAxes.length && (
                  <Typography variant="caption" sx={{ color: '#8a5300', display: 'block' }}>
                    ⚠ {nonScaleAxes.join(', ')} {nonScaleAxes.length === 1 ? 'is an ordered set' : 'are ordered sets'} of peers
                    rather than a scale — the picklist fixes their order but not any magnitude, so radial distance on
                    {nonScaleAxes.length === 1 ? ' that axis' : ' those axes'} means only &ldquo;further down the list&rdquo;.
                  </Typography>
                )}
              </Paper>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2 }}>
                <Paper sx={{ p: 1 }}>{renderRadar()}</Paper>

                <Box>
                  <Paper sx={{ p: 1.5, mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 13, mb: 0.75 }}>Subjects</Typography>
                    <Stack spacing={0.75}>
                      {radar.subjects.map((s) => {
                        const missing = s.values
                          .map((v, i) => (v ? null : radar.axes[i].dim.label))
                          .filter(Boolean);
                        return (
                          <Stack key={s.key} direction="row" spacing={1} alignItems="flex-start">
                            <Box style={{ backgroundColor: s.color }} sx={{ width: 12, height: 12, borderRadius: '50%', mt: 0.4, flex: '0 0 auto' }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, cursor: s.id ? 'pointer' : 'default' }}
                                onClick={() => (s.id && onOpen ? onOpen(s.id) : null)}
                              >
                                {s.label}
                                {s.members > 1 ? ` (${num(s.members)} records)` : ''}
                              </Typography>
                              <Typography variant="caption" sx={{ color: missing.length ? '#8a5300' : GREY, display: 'block' }}>
                                {missing.length
                                  ? `no value on ${missing.length} of ${radar.axes.length} axes: ${missing.join(', ')}`
                                  : `a value on all ${radar.axes.length} axes`}
                              </Typography>
                              {showRadarGaps && s.values.map((v, i) => (
                                <Typography key={`${s.key}-v-${i}`} variant="caption" sx={{ color: GREY, display: 'block' }}>
                                  {radar.axes[i].dim.label}: {v ? v.raw : '— not captured'}
                                </Typography>
                              ))}
                            </Box>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Paper>

                  <Paper sx={{ p: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 13, mb: 0.75 }}>Axis coverage</Typography>
                    {radar.coverage.map((c) => (
                      <Typography key={c.label} variant="caption" sx={{ color: c.withValue < c.total ? '#8a5300' : GREY, display: 'block' }}>
                        <strong>{c.label}</strong> — {c.withValue} of {c.total} subjects plotted
                        {c.withValue < c.total ? `; ${c.total - c.withValue} excluded for missing ${c.label}` : ''}
                        {' · '}{c.levels} levels ({trunc(c.first, 18)} → {trunc(c.last, 18)})
                        {c.scale ? '' : ' · ⚠ not a scale'}
                        {c.extras ? ` · ${c.extras} value(s) outside the picklist appended at the end` : ''}
                      </Typography>
                    ))}
                    <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.75 }}>
                      {radarMode === 'groups'
                        ? 'Group positions are the mean over the records in the group that have a value on that axis; the counts behind each mean are in the CSV export.'
                        : 'A subject with no value on an axis has no vertex there — the outline is bridged with a dashed edge instead of being drawn at the centre.'}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </div>
          )}
        </Box>
      )}
    </Box>
  );
}
